import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Avatar,
  Typography,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  Chip,
  Stack,
  IconButton,
  LinearProgress,
  Alert,
  Paper,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Business as BusinessIcon,
  Language as LanguageIcon,
  Message as MessageIcon,
  Api as ApiIcon,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { GET_RCS_BOTS } from "../../config/urls";
import axios from "axios";
import ButtonOne from "../../common/normal-button";
import { ColorsConstants } from "../../cssUtils/constants";

// Styled components
const StatCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: "center",
  borderRadius: theme.shape.borderRadius * 2,
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
  },
}));

const BotCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  cursor: "pointer",
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: theme.shadows[4],
  },
}));

const BotList = ({ bots, onViewBot, onCreateBot, onViewBrands, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRegion, setFilterRegion] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterMessageType, setFilterMessageType] = useState("ALL");
  const permissionsFromStorage = JSON.parse(
    localStorage.getItem("permissions") || "[]",
  );


  const filteredBots = bots.filter((bot) => {
    const matchesSearch =
      bot.bot_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bot.brand_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bot.short_description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRegion = filterRegion === "ALL" || bot.region === filterRegion;
    const matchesStatus = filterStatus === "ALL" || bot.status === filterStatus;
    const matchesMessageType =
      filterMessageType === "ALL" || bot.message_type === filterMessageType;

    return (
      matchesSearch && matchesRegion && matchesStatus && matchesMessageType
    );
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
        return "success";
      case "SUBMITTED":
        return "warning";
      case "DRAFT":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "APPROVED":
        return "Approved";
      case "SUBMITTED":
        return "Submitted";
      case "DRAFT":
        return "Draft";
      default:
        return status;
    }
  };

  const getRegionLabel = (region) => {
    switch (region) {
      case "INDIA":
        return "India";
      case "ROW":
        return "Rest of World";
      default:
        return region;
    }
  };

  const getMessageTypeLabel = (type) => {
    switch (type) {
      case "PROMOTIONAL":
        return "Promotional";
      case "OTP":
        return "OTP";
      case "MULTI_USE":
        return "Multi-Use";
      case "TRANSACTIONAL":
        return "Transactional";
      default:
        return type;
    }
  };

  const stats = {
    total: bots.length,
    approved: bots.filter((b) => b.status === "APPROVED").length,
    submitted: bots.filter((b) => b.status === "SUBMITTED").length,
    draft: bots.filter((b) => b.status === "DRAFT").length,
  };

  return (
    <Box>
      {/* Header Section */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={2}
        sx={{ mb: 4 }}
      >
        <Box>
          <Typography gutterBottom sx={{ fontWeight: 600, fontSize: 24 }}>
            RCS Bots
          </Typography>
          <Typography
            color="text.secondary"
            sx={{ fontWeight: 400, fontSize: 14 }}
          >
            Manage your RCS Business Messaging bots and templates
          </Typography>
        </Box>

        <Stack direction="row" spacing={2}>
          {/* <ButtonOne
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            disabled={loading}
          >
            Refresh
          </ButtonOne> */}
  <ButtonOne variant="outlined" onClick={onViewBrands}>
    Brands
  </ButtonOne>
{/* With no bots the empty state carries the only Create Bot CTA */}
{bots.length > 0 && permissionsFromStorage.includes("rcs_bot_create.create") ? (
  <ButtonOne
    variant="contained"
    startIcon={<AddIcon />}
    onClick={onCreateBot}
  >
    Create New Bot
  </ButtonOne>
) : null}
        </Stack>
      </Stack>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard elevation={1}>
            <Typography
              variant="h3"
              component="div"
              sx={{ fontWeight: 700, mb: 1 }}
            >
              {stats.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Bots
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            elevation={1}
            sx={{ borderTop: 4, borderColor: "success.main" }}
          >
            <Typography
              variant="h3"
              component="div"
              sx={{ fontWeight: 700, mb: 1, color: "success.main" }}
            >
              {stats.approved}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Approved
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            elevation={1}
            sx={{ borderTop: 4, borderColor: "warning.main" }}
          >
            <Typography
              variant="h3"
              component="div"
              sx={{ fontWeight: 700, mb: 1, color: "warning.main" }}
            >
              {stats.submitted}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Submitted
            </Typography>
          </StatCard>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            elevation={1}
            sx={{ borderTop: 4, borderColor: "grey.500" }}
          >
            <Typography
              variant="h3"
              component="div"
              sx={{ fontWeight: 700, mb: 1, color: "grey.700" }}
            >
              {stats.draft}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Draft
            </Typography>
          </StatCard>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "#DCDFE5",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
          <TextField
  fullWidth
  placeholder="Search bots, brands, or descriptions..."
  value={searchTerm}
  onChange={(e) => setSearchTerm(e.target.value)}
  sx={{
    "& .MuiOutlinedInput-root": {
      borderRadius: "8px",
      height: "56px",
      "& input": {
        fontSize: "14px",
        padding: "16px 14px 16px 8px", // Reduced left padding (was 14px, now 8px)
      },
      "& fieldset": {
        borderColor: "#DCDFE5",
      },
      "&:hover fieldset": {
        borderColor: "#DCDFE5",
      },
      "&.Mui-focused fieldset": {
        borderColor: ColorsConstants.primaryBackgroundColor,
      },
    },
  }}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start" sx={{ mr: 0.5 }}> {/* Reduced right margin */}
        <SearchIcon sx={{ color: "#9AA6B8", fontSize: 18 }} />
      </InputAdornment>
    ),
  }}
/>
          </Grid>

          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth>
              <InputLabel
                id="region-filter-label"
                sx={{
                  fontSize: "14px",
                  transform: "translate(14px, 18px) scale(1)",
                  "&.MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              >
                Region
              </InputLabel>
              <Select
                labelId="region-filter-label"
                value={filterRegion}
                label="Region"
                onChange={(e) => setFilterRegion(e.target.value)}
                sx={{
                  borderRadius: "8px",
                  height: "56px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#DCDFE5",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#DCDFE5",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: ColorsConstants.primaryBackgroundColor,
                  },
                  "& .MuiSelect-select": {
                    fontSize: "14px",
                    padding: "16px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  },
                }}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <LanguageIcon sx={{ color: "#9AA6B8", fontSize: 18 }} />
                  </InputAdornment>
                }
              >
                <MenuItem value="ALL" sx={{ fontSize: "14px" }}>
                  All Regions
                </MenuItem>
                <MenuItem value="INDIA" sx={{ fontSize: "14px" }}>
                  India
                </MenuItem>
                <MenuItem value="ROW" sx={{ fontSize: "14px" }}>
                  Rest of World
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={2.5}>
            <FormControl fullWidth>
              <InputLabel
                id="status-filter-label"
                sx={{
                  fontSize: "14px",
                  transform: "translate(14px, 18px) scale(1)",
                  "&.MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              >
                Status
              </InputLabel>
              <Select
                labelId="status-filter-label"
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
                sx={{
                  borderRadius: "8px",
                  height: "56px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#DCDFE5",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#DCDFE5",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: ColorsConstants.primaryBackgroundColor,
                  },
                  "& .MuiSelect-select": {
                    fontSize: "14px",
                    padding: "16px 14px",
                  },
                }}
              >
                <MenuItem value="ALL" sx={{ fontSize: "14px" }}>
                  All Status
                </MenuItem>
                <MenuItem value="DRAFT" sx={{ fontSize: "14px" }}>
                  Draft
                </MenuItem>
                <MenuItem value="SUBMITTED" sx={{ fontSize: "14px" }}>
                  Submitted
                </MenuItem>
                <MenuItem value="APPROVED" sx={{ fontSize: "14px" }}>
                  Approved
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel
                id="type-filter-label"
                sx={{
                  fontSize: "14px",
                  transform: "translate(14px, 18px) scale(1)",
                  "&.MuiInputLabel-shrink": {
                    transform: "translate(14px, -9px) scale(0.75)",
                  },
                }}
              >
                Message Type
              </InputLabel>
              <Select
                labelId="type-filter-label"
                value={filterMessageType}
                label="Message Type"
                onChange={(e) => setFilterMessageType(e.target.value)}
                sx={{
                  borderRadius: "8px",
                  height: "56px",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#DCDFE5",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#DCDFE5",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: ColorsConstants.primaryBackgroundColor,
                  },
                  "& .MuiSelect-select": {
                    fontSize: "14px",
                    padding: "16px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  },
                }}
                startAdornment={
                  <InputAdornment position="start" sx={{ ml: 1 }}>
                    <MessageIcon sx={{ color: "#9AA6B8", fontSize: 18 }} />
                  </InputAdornment>
                }
              >
                <MenuItem value="ALL" sx={{ fontSize: "14px" }}>
                  All Types
                </MenuItem>
                <MenuItem value="PROMOTIONAL" sx={{ fontSize: "14px" }}>
                  Promotional
                </MenuItem>
                <MenuItem value="OTP" sx={{ fontSize: "14px" }}>
                  OTP
                </MenuItem>
                <MenuItem value="MULTI_USE" sx={{ fontSize: "14px" }}>
                  Multi-Use
                </MenuItem>
                <MenuItem value="TRANSACTIONAL" sx={{ fontSize: "14px" }}>
                  Transactional
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Bot Cards Grid */}
      {filteredBots.length === 0 ? (
        <Paper sx={{ p: 8, textAlign: "center", borderRadius: 2 }}>
          <BusinessIcon sx={{ fontSize: 64, color: "grey.400", mb: 2 }} />
          <Typography variant="h6" gutterBottom color="text.secondary">
            {bots.length === 0
              ? "No bots created yet"
              : "No bots match your filters"}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {bots.length === 0
              ? "Get started by creating your first RCS bot"
              : "Try adjusting your search or filters"}
          </Typography>
          {bots.length === 0 && permissionsFromStorage.includes("rcs_bot_create.create") && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateBot}
              size="large"
            >
              Create Your First Bot
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredBots.map((bot) => (
            <Grid item xs={12} sm={6} lg={4} key={bot.id}>
              <BotCard elevation={2} onClick={() => onViewBot(bot)}>
                <CardHeader
                  avatar={
                    <Avatar
                      src={bot.bot_logo}
                      sx={{
                        bgcolor: "primary.main",
                        width: 56,
                        height: 56,
                      }}
                    >
                      {bot.bot_name?.[0]}
                    </Avatar>
                  }
                  action={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        pr: 1,
                        pt: 1,
                      }}
                    >
                      <Chip
                        label={getStatusLabel(bot.status)}
                        color={getStatusColor(bot.status)}
                        size="small"
                        sx={{
                          fontWeight: 500,
                          maxWidth: "100px",
                          borderRadius: "6px",
                          "& .MuiChip-label": {
                            px: 1,
                            fontSize: 10,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }}
                      />
                    </Box>
                  }
                  title={
                    <Tooltip title={bot.bot_name} arrow placement="top">
                      <Typography
                        component="div"
                        noWrap
                        sx={{ fontSize: 16, fontWeight: 400 }}
                      >
                        {bot.bot_name}
                      </Typography>
                    </Tooltip>
                  }
                  subheader={
                    <Typography
                      sx={{ fontSize: 12, fontWeight: 400 }}
                      color="text.secondary"
                      noWrap
                    >
                      {bot.brand_name}
                    </Typography>
                  }
                  sx={{
                    bgcolor: "#F0F5FF",
                    borderTopLeftRadius: "inherit",
                    borderTopRightRadius: "inherit",
                    "& .MuiCardHeader-content": {
                      overflow: "hidden",
                    },
                    "& .MuiCardHeader-action": {
                      marginTop: 0,
                      marginRight: 0,
                      alignSelf: "flex-start",
                    },
                  }}
                />

                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {bot.short_description}
                  </Typography>

                  <Stack spacing={1} sx={{ mt: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <LanguageIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                      <Typography variant="body2">
                        <strong>Region:</strong> {getRegionLabel(bot.region)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <MessageIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                      <Typography variant="body2">
                        <strong>Type:</strong>{" "}
                        {getMessageTypeLabel(bot.message_type)}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <ApiIcon
                        fontSize="small"
                        sx={{ mr: 1, color: "text.secondary" }}
                      />
                      <Typography variant="body2">
                        <strong>Brand:</strong>{" "}
                        {bot.brand_details?.brand_name || ""}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>

                <Divider />

                <CardActions sx={{ justifyContent: "space-between", p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Created: {new Date(bot.created_at).toLocaleDateString()}
                  </Typography>
                  <Button
                    size="small"
                    endIcon={<ViewIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      onViewBot(bot);
                    }}
                  >
                    View Details
                  </Button>
                </CardActions>
              </BotCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Results Count */}
      {filteredBots.length > 0 && (
        <Box
          sx={{
            mt: 4,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {filteredBots.length} of {bots.length} bots
          </Typography>
          {searchTerm ||
          filterRegion !== "ALL" ||
          filterStatus !== "ALL" ||
          filterMessageType !== "ALL" ? (
            <Button
              size="small"
              onClick={() => {
                setSearchTerm("");
                setFilterRegion("ALL");
                setFilterStatus("ALL");
                setFilterMessageType("ALL");
              }}
            >
              Clear all filters
            </Button>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default BotList;
